<template>
  <VDataGridV2
    entity-type="productCategory"
    group-children-property="offers"
    :loader="dataLoader"
    @row:dblclick="OffersService.showOfferCard"
    :context-menu="$core.currentPartner.isSeller ? OffersService.contextMenu : null"
    :group-context-menu="$core.currentPartner.isSeller ? OffersService.groupContextMenu : null"
    paginated
    class="offers-registry-expanded"
  >
    <VDataGridGroupV2>
      <template #default="{ parent: row }">
        <div> {{ row.productCategory.name}} </div>

        <VButton
          @click="() => toggleCategoryBookmark(row.productCategory)"
          class="bookmark-btn">
          <VIcon :material="row.productCategory.isBookmarked ? 'favorite' : 'favorite_border'"/>
        </VButton>

      </template>
    </VDataGridGroupV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner.isBuyer"
      id="id"
      label="№"
      width="80px"
    >
      <template #default="{ row }">
        <VLink @click="OffersService.showOfferCard(row)">{{ row.id }}</VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 v-if="$core.currentPartner.isBuyer"
                       id="seller"
                       width="220px"
                       label="Поставщик"
    >
      <template #default="{ row }">
        <div>{{ row.sellerCompany.name }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 v-if="$core.currentPartner.isBuyer"
                       id="name"
                       label="Сырьё"
                       width="425px"
                       filterable
                       filter-send-name="pattern"
                       :filter-props="{ placeholder: 'Введите наименование сырья', clearable: true }"
    >
      <template #default="{ row }">
        <div>{{ row.productCategory.name }}</div>
        <div
          class="description ellipsis"
          :title="row.manufacturer.name"
        >
          {{ row.manufacturer.name }}
        </div>
        <div class="description">
          Годен до {{ row.productExpirationDate?.format() }}
        </div>

        <VBadges>
          <template v-if="row.isAcceptingPreorder">
            <VBadge>
              Доступен предзаказ от {{ row.preorderMinQty?.format(row.unit) || '--' }}
            </VBadge>
            <VBadge>
              Страховой запас: {{ row.preorderMonthlyReserve?.format(row.unit) || 'нет' }}
            </VBadge>
          </template>
          <VBadge
            v-if="row.isSamples">
            Образец
          </VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 v-if="$core.currentPartner.isSeller"
                       id="name"
                       width="600px"
                       label="Наименование сырья"
                       filterable
                       filter-send-name="pattern"
                       :filter-props="{ placeholder: 'Введите наименование сырья', clearable: true }"
    >
      <template #default="{ row }">
        <VLink
          @click="OffersService.showOfferCard(row)"
        >
          {{ row.productCategory.name }}
        </VLink>
        <div
          v-if="row.isMine"
          class="description ellipsis"
        >
          Производитель: {{ row.manufacturer.name }}
        </div>
        <div class="description">
          Годен до {{ $core.utils.date.format(row.productExpirationDate) }}
        </div>

        <VBadges v-if="row.isMine">
          <VBadge>
            Моё
          </VBadge>
          <VBadge v-if="row.isSamples">
            Образец
          </VBadge>
          <VBadge v-if="row.updatedAt">
            Актуален до {{ $core.utils.date.format(getRelevantToDate(row)) }}
          </VBadge>
          <VBadge v-if="!row.isPublished">
            Не опубликовано
          </VBadge>
          <VBadge v-if="row.isAcceptingPreorder">
            Доступен предзаказ от {{ row.preorderMinQty?.format(row.unit) || '--' }}
          </VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="deliveryDays"
      width="220px"
    >
      <template #label>
        Интервал получения<br>
        <div class="description">
          Кто осуществляет доставку
        </div>
      </template>
      <template #default="{ row }">
        <div
          v-if="row.receiptPeriod"
          class="title"
        >
          {{ row.receiptPeriod.toString({ prefix: 'с', middle: 'по' }) }}
        </div>
        <div class="description">
          Доставляется {{ row.deliveryType.name.toLowerCase() }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="qty"
      label="Доступно"
      width="180px"
      content-align="right"
    >
      <template #default="{ row }">
        <div class="title">
          {{ row.qtyLeft.format(row.unit) }}
        </div>
        <div class="description">
          Упаковка:
          <template v-if="row.package && row.package.name">{{ row.package.name }}</template>
          <template v-else>не указано</template>
        </div>
        <div class="description">
          Фасовка:
          <template v-if="row.packageVolume">{{ row.packageVolume }} {{ row.unit.name }}</template>
          <template v-else>не указано</template>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 v-if="$core.currentPartner.isBuyer"
                       id="price"
                       width="160px"
                       content-align="right"
                       label="Цена + доставка, с НДС"
                       header-class="nowrap"
    >
      <template #default="{ row }">
        <template v-if="row.priceFullBuyer">
          {{ row.priceFullBuyer.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate(), row.unit) }}

          <div
            v-if="row.offerCurrencyCode !== $core.displayedCurrencyCode"
            class="description"
          >
            Пересчитано из {{ row.priceFullBuyer.format(row.offerCurrencyCode, row.unit) }}
          </div>
          <div v-else class="description">Валюта предложения</div>
        </template>
        <template v-else>--</template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 v-if="$core.currentPartner.isSeller"
                       id="price"
                       label="Цена + доставка, с НДС"
                       width="200px"
                       content-align="right"
                       header-class="nowrap"
    >
      <template #default="{ row }">
        <template v-if="row.priceFullSeller">
          <div>{{ row.priceFullSeller.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate(), row.unit) }}</div>

          <div
            v-if="row.offerCurrencyCode !== $core.displayedCurrencyCode"
            class="description"
          >
            Пересчитано из {{ row.priceFullSeller.format(row.offerCurrencyCode, row.unit) }}
          </div>
          <div v-else class="description">Валюта предложения</div>
        </template>
        <template v-else>--</template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 v-if="$core.currentPartner.isBuyer">
      <template #default="{ row }">
        <VButton @click="onBuyButtonClick(row)" material="add_shopping_cart" />
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2 v-if="$core.currentPartner.isSeller">
      <template #default>
        <VButton @click="OffersService.showOfferEditDialog()">
          Создать предложение
        </VButton>
      </template>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<style lang="scss" src="./OffersRegistryExpanded.scss" />

<script lang="ts">

import DataGridLoader    from '@/lib/components/VDataGridV2/DataGridLoader';
import OffersService     from "@/domains/Offers/OffersService";
import Offer             from "@/domains/Offers/Offer";
import BookmarksService  from "@/domains/Bookmark/BookmarksService";

export default defineComponent({
  name: 'OffersRegistryExpanded',

  props: {
    params: {
      type: Object as () => Record<string, unknown>,
      default: () => ({}),
    },
  },

  setup() {
    return {
      OffersService,
      Offer,
    }
  },

  data() {
    return {
      dataLoader: new DataGridLoader(OffersService.getListPaginated)
        .useReactiveSource(() => this.params)
        .onAfterLoad((result) => {
          this.dataLoader.data = this.groupByCategory(result.data);
        }),
    };
  },

  methods: {

    /**
     * @todo легаси, нужно убить — рассчитывает дату актуальности предложения
     * как updatedAt + 19 дней.
     * !!! остается до выяснения обстоятельств) пока не понятно, а легаси ли
     */
    getRelevantToDate(offer: any): Date {
      const fullYear = offer.updatedAt.getFullYear();
      const month = offer.updatedAt.getMonth();
      const date = offer.updatedAt.getDate() + 19;
      return new Date(fullYear, month, date, 0, 0, 0, 0);
    },

    groupByCategory(offers) {
      const map = new Map();

      for (const offer of offers) {
        const categoryId = offer.productCategory.id;

        if (!map.has(categoryId)) {
          map.set(categoryId, {
            id:              categoryId,
            productCategory: offer.productCategory,
            offers:          [],
          });
        }

        map.get(categoryId).offers.push(offer);
      }

      return [...map.values()];
    },

    toggleCategoryBookmark(category) {
      const toggleAction = category.isBookmarked ? 'delete' : 'add';

      const success = BookmarksService[toggleAction](category.id);

      if (success) {
        this.dataLoader.refresh();
      }
    },

    async onBuyButtonClick(row) {
      const isNeedToUpdate = await OffersService.showOrderCreateDialogMinimized(row)

      if (isNeedToUpdate) {
        this.dataLoader.refresh()
      }
    },

    refresh(): void {
      this.dataLoader.refresh();
    },
  },
});
</script>
